import { useProfile } from './useProfile.js';

/**
 * Atalhos da Início, do jeito da pessoa: como aparecem (visíveis, discretos ou
 * tela limpa), de que lado e em que ordem. Mudam só por opção com nome, na tela
 * "Atalhos" dos Ajustes, nunca por gesto escondido (pesquisa P, gestos do palco).
 * Ficam em `profile.preferences.home` e num espelho local, para a Início não
 * piscar com o padrão enquanto o perfil chega.
 */
export type RailMode = 'visible' | 'discreet' | 'clean';
export type RailSide = 'right' | 'left';
export type RailKey = 'agenda_day' | 'bills' | 'people' | 'deliveries';
/** Hoje: o Hoje fixo no meio, com o personagem em busto acima (padrão). Palco: o personagem em tela cheia. */
export type HomeLayout = 'hoje' | 'palco';

export type HomePrefs = {
  layout: HomeLayout;
  rail: RailMode;
  side: RailSide;
  order: RailKey[];
  labels: boolean;
};

export const RAIL_KEYS: readonly RailKey[] = ['agenda_day', 'bills', 'people', 'deliveries'];
export const HOME_PREFS_DEFAULT: HomePrefs = {
  layout: 'hoje',
  rail: 'visible',
  side: 'right',
  order: [...RAIL_KEYS],
  labels: true,
};

const LOCAL_KEY = 'secretario.palco.atalhos';

/** Lê o que estiver guardado, aceitando só valores válidos e completando o resto. */
export function parseHomePrefs(preferences: Record<string, unknown> | undefined | null): HomePrefs {
  const raw = (preferences?.home ?? null) as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') return HOME_PREFS_DEFAULT;
  const rail: RailMode =
    raw.rail === 'discreet' || raw.rail === 'clean' || raw.rail === 'visible'
      ? raw.rail
      : 'visible';
  const side: RailSide = raw.side === 'left' ? 'left' : 'right';
  const given = Array.isArray(raw.order)
    ? raw.order.filter((item): item is RailKey => RAIL_KEYS.includes(item as RailKey))
    : [];
  const order = [...new Set(given), ...RAIL_KEYS.filter((key) => !given.includes(key))];
  // Nomes dos botões: só saem por escolha, e só no modo Discretos (a Tela limpa nunca tem).
  const labels = rail === 'visible' ? true : raw.labels !== false;
  const layout: HomeLayout = raw.layout === 'palco' ? 'palco' : 'hoje';
  return { layout, rail, side, order, labels };
}

export function sameHomePrefs(a: HomePrefs, b: HomePrefs): boolean {
  return (
    a.layout === b.layout &&
    a.rail === b.rail &&
    a.side === b.side &&
    a.order.join() === b.order.join() &&
    a.labels === b.labels
  );
}

export function readHomePrefsLocal(): HomePrefs | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    return raw ? parseHomePrefs({ home: JSON.parse(raw) as unknown }) : null;
  } catch {
    return null;
  }
}

export function writeHomePrefsLocal(prefs: HomePrefs): void {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs));
  } catch {
    /* sem armazenamento: o perfil continua valendo */
  }
}

/** As preferências em vigor: do perfil quando já chegou, do espelho local antes disso. */
export function useHomePrefs(): HomePrefs {
  const profile = useProfile().data;
  if (profile) {
    const prefs = parseHomePrefs(profile.preferences);
    const local = readHomePrefsLocal();
    if (!local || !sameHomePrefs(local, prefs)) writeHomePrefsLocal(prefs);
    return prefs;
  }
  return readHomePrefsLocal() ?? HOME_PREFS_DEFAULT;
}
